import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, Extension, mergeAttributes, markInputRule } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import Italic from '@tiptap/extension-italic';
import Heading from '@tiptap/extension-heading';
import Strike from '@tiptap/extension-strike';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import StarterKit from '@tiptap/starter-kit';
import Blockquote from '@tiptap/extension-blockquote';
import { Markdown } from 'tiptap-markdown';

export interface RichTextEditorHandle {
    focus: () => void;
    toggleBold: () => void;
    toggleUnderline: () => void;
    toggleBulletList: () => void;
    insertText: (text: string) => void;
    appendText: (text: string) => void;
    setContent: (markdown: string) => void;
    getMarkdown: () => string;
    getTextBeforeCursor: () => string;
    getCursorScreenCoords: () => { x: number; y: number };
    replaceWordBeforeCursor: (query: string, replacement: string) => void;
    getSelectedText: () => string;
    replaceSelection: (text: string) => void;
    transformSelectionText: (fn: (text: string) => string) => void;
    reflowSelection: () => void;
    findMatches: (query: string, caseSensitive?: boolean) => number;
    goToMatch: (query: string, index: number, caseSensitive?: boolean) => number;
    replaceCurrent: (query: string, replacement: string, matchIndex: number, caseSensitive?: boolean) => boolean;
    replaceAll: (query: string, replacement: string, caseSensitive?: boolean) => number;
    jumpToText: (blockText: string, charOffset: number) => void;
    focusAtCoords: (clientX: number, clientY: number) => void;
    getScrollElement: () => HTMLElement | null;
}

interface RichTextEditorProps {
    initialValue: string;
    onChange: (markdown: string) => void;
    onInput?: () => void;  // cheap dirty signal — called on every keystroke, no serialization
    placeholder?: string;
    readOnly?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    className?: string;
    style?: React.CSSProperties;
    autoFocus?: boolean;
    onKeyDown?: (event: KeyboardEvent) => boolean;
    onActiveFormatsChange?: (formats: { bold: boolean }) => void;
    searchQuery?: string;
    searchCaseSensitive?: boolean;
    currentMatchIndex?: number;
}

type SearchHighlightState = { query: string; caseSensitive: boolean; currentIndex?: number };
const searchHighlightKey = new PluginKey<SearchHighlightState>('searchHighlight');

const SearchHighlightExtension = Extension.create({
    name: 'searchHighlight',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: searchHighlightKey,
                state: {
                    init: () => ({ query: '', caseSensitive: false, currentIndex: -1 }),
                    apply(tr, prev) {
                        const next = tr.getMeta(searchHighlightKey);
                        return next !== undefined ? next : prev;
                    },
                },
                props: {
                    decorations(state) {
                        const { query, caseSensitive, currentIndex = -1 } = searchHighlightKey.getState(state) ?? { query: '', caseSensitive: false, currentIndex: -1 };
                        if (!query.trim()) return DecorationSet.empty;
                        const q = caseSensitive ? query : query.toLowerCase();
                        const decorations: Decoration[] = [];
                        let matchCount = 0;
                        state.doc.descendants((node, pos) => {
                            if (!node.isText) return;
                            const text = caseSensitive ? node.text! : node.text!.toLowerCase();
                            let idx = 0;
                            while ((idx = text.indexOf(q, idx)) !== -1) {
                                const isCurrent = matchCount === currentIndex;
                                decorations.push(
                                    Decoration.inline(pos + idx, pos + idx + q.length, {
                                        class: isCurrent ? 'find-replace-highlight find-replace-highlight-active' : 'find-replace-highlight',
                                    })
                                );
                                matchCount++;
                                idx++;
                            }
                        });
                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },
});

// Custom paragraph node using Node.create (not Paragraph.extend) to avoid
// inheriting TipTap v3's renderMarkdown/parseMarkdown which conflict with tiptap-markdown.
//
// priority: 1000 matches @tiptap/extension-paragraph's own priority so that
// 'paragraph' is the first block type in ProseMirror's ContentMatch transition
// list for the 'block+' expression. fillBefore(block+) picks paragraph first,
// and paragraph.createAndFill() returns an empty node instantly (inline*, min 0),
// breaking the blockquote→fillBefore(block+)→blockquote→… infinite loop that
// occurs when a lower-priority paragraph lets blockquote become the defaultType.
//
// Empty paragraphs serialize as NBSP (U+00A0) so they survive round-trip.
// markdown-it treats NBSP as non-blank (only ASCII whitespace collapses paragraphs).
const BlankLineParagraph = Node.create({
    name: 'paragraph',
    priority: 1000,
    group: 'block',
    content: 'inline*',
    parseHTML() {
        return [{ tag: 'p' }];
    },
    renderHTML({ HTMLAttributes }: any) {
        return ['p', mergeAttributes(HTMLAttributes), 0];
    },
    addCommands() {
        return {
            setParagraph: () => ({ commands }: any) => commands.setNode(this.name),
        };
    },
    addKeyboardShortcuts() {
        return {
            'Mod-Alt-0': () => this.editor.commands.setParagraph(),
        };
    },
    addStorage() {
        return {
            markdown: {
                serialize(state: any, node: any) {
                    if (node.childCount === 0) {
                        state.write(' '); // NBSP: survives markdown round-trip
                    } else {
                        state.renderInline(node);
                    }
                    state.closeBlock(node);
                },
            },
        };
    },
});

// Pre-process legacy notes: convert 3+ newlines (textarea blank lines) to
// NBSP paragraphs so existing notes load with blank lines preserved.
const preProcessMarkdown = (md: string): string =>
    md.replace(/\n{3,}/g, () => '\n\n \n\n');

// Known limitation: `~~text~~` shows as strikethrough in editor, underline in NodeCard view. Phase 1 fix.
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(({
    initialValue,
    onChange,
    onInput,
    readOnly = false,
    onFocus,
    onBlur,
    className = '',
    style,
    autoFocus = false,
    onKeyDown,
    onActiveFormatsChange,
    searchQuery = '',
    searchCaseSensitive = false,
    currentMatchIndex = -1,
}, ref) => {
    const onKeyDownRef = useRef(onKeyDown);
    onKeyDownRef.current = onKeyDown;
    const onActiveFormatsChangeRef = useRef(onActiveFormatsChange);
    onActiveFormatsChangeRef.current = onActiveFormatsChange;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onInputRef = useRef(onInput);
    onInputRef.current = onInput;
    const lastBoldRef = useRef(false);
    const scrollDivRef = useRef<HTMLDivElement>(null);

    // Override Mod-b: after bolding a selection, immediately clear stored marks
    // so subsequent typed text does not inherit bold from the adjacent character.
    const BoldNoCarryOver = Extension.create({
        name: 'boldNoCarryOver',
        priority: 200,
        addKeyboardShortcuts() {
            return {
                'Mod-b': () => {
                    const { selection } = this.editor.state;
                    const hadSelection = !selection.empty;
                    this.editor.commands.toggleBold();
                    if (hadSelection) {
                        this.editor.view.dispatch(
                            this.editor.state.tr.setStoredMarks([])
                        );
                    }
                    return true;
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            BoldNoCarryOver,
            StarterKit.configure({
                paragraph: false,    // replaced by BlankLineParagraph
                blockquote: false,   // replaced below without input rule
                italic: false,       // replaced below without input rule
                heading: false,      // replaced below without input rule
                strike: false,       // replaced below without input rule
                bold: false,         // replaced below without input rule
                bulletList: false,   // replaced below without input rule
                orderedList: false,  // replaced below without input rule
                listItem: false,     // replaced below (added explicitly after BulletList/OrderedList)
                code: false,         // replaced below without input rule
                codeBlock: false,    // replaced below without input rule
            }),
            Italic.extend({ addInputRules: () => [] }),
            Blockquote.extend({ addInputRules: () => [] }),
            Heading.configure({ levels: [1, 2, 3] }).extend({ addInputRules: () => [] }),
            Strike.extend({ addInputRules: () => [] }),
            Bold.extend({ addInputRules: () => [] }),
            BulletList.extend({ addInputRules: () => [] }),
            OrderedList.extend({ addInputRules: () => [] }),
            ListItem,
            Code.extend({ addInputRules: () => [] }),
            CodeBlock.extend({ addInputRules: () => [] }),
            BlankLineParagraph,
            SearchHighlightExtension,
            Markdown.configure({
                html: false,
                tightLists: true,
                bulletListMarker: '-',
                transformPastedText: true,
                transformCopiedText: false,
            }),
        ],
        content: preProcessMarkdown(initialValue || ''),
        editable: !readOnly,
        autofocus: autoFocus ? 'end' : false,
        onUpdate: () => {
            // Fire cheap dirty signal only — NO serialization here.
            // getMarkdown() is O(doc size) and blocks the main thread; calling it on every
            // keystroke (even debounced) causes the periodic stutter the user feels.
            // The parent debounces its own save and calls richEditorRef.getMarkdown() once
            // when the idle window expires.
            onInputRef.current?.();
        },
        onSelectionUpdate: ({ editor }) => {
            const { selection, storedMarks } = editor.state;
            if (!selection.empty || !storedMarks?.length) return;
            const boldType = editor.schema.marks.bold;
            if (!storedMarks.some(m => m.type === boldType)) return;
            // If character after cursor is not bold, we're at the end of a bold region — stop carrying bold forward
            const nodeAfter = selection.$from.nodeAfter;
            const hasBoldAfter = nodeAfter ? boldType.isInSet(nodeAfter.marks) : false;
            if (!hasBoldAfter) {
                editor.view.dispatch(editor.state.tr.setStoredMarks(storedMarks.filter(m => m.type !== boldType)));
            }
        },
        onTransaction: ({ editor }) => {
            const bold = editor.isActive('bold');
            if (bold !== lastBoldRef.current) {
                lastBoldRef.current = bold;
                onActiveFormatsChangeRef.current?.({ bold });
            }
        },
        onFocus: () => onFocus?.(),
        onBlur: () => { onActiveFormatsChangeRef.current?.({ bold: false }); onBlur?.(); },
        editorProps: {
            handleKeyDown: (_view: any, event: KeyboardEvent) => {
                return onKeyDownRef.current?.(event) ?? false;
            },
            attributes: {
                spellcheck: 'false',
                autocorrect: 'off',
                autocapitalize: 'off',
                class: [
                    'outline-none w-full min-h-full pb-32',
                    'text-[1em] leading-relaxed',
                    'text-gray-700 dark:text-gray-200',
                    '[&>p]:my-2',
                    '[&>ul]:list-disc [&>ul]:pl-5 [&>ul]:my-1',
                    '[&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:my-1',
                    '[&>h1]:text-2xl [&>h1]:font-bold [&>h1]:my-2',
                    '[&>h2]:text-xl [&>h2]:font-bold [&>h2]:my-2',
                    '[&>h3]:text-lg [&>h3]:font-semibold [&>h3]:my-1',
                    '[&_strong]:font-bold',
                    '[&_em]:italic',
                    '[&_u]:underline',
                    '[&_s]:line-through',
                    '[&_code]:font-mono [&_code]:text-sm [&_code]:bg-gray-100 [&_code]:dark:bg-slate-800 [&_code]:px-1 [&_code]:rounded',
                ].join(' '),
            },
        },
    });

    // Sync readOnly prop → Tiptap editable without remounting.
    // This is what enables keeping the editor always mounted across view/edit mode switches.
    useEffect(() => {
        if (editor && editor.isEditable !== !readOnly) {
            editor.setEditable(!readOnly);
        }
    }, [editor, readOnly]);

    // Sync search highlight query → ProseMirror decoration plugin
    useEffect(() => {
        if (!editor) return;
        editor.view.dispatch(
            editor.state.tr.setMeta(searchHighlightKey, { query: searchQuery || '', caseSensitive: searchCaseSensitive, currentIndex: currentMatchIndex })
        );
    }, [editor, searchQuery, searchCaseSensitive, currentMatchIndex]);

    useImperativeHandle(ref, () => {
        if (!editor) {
            return {
                focus: () => {},
                toggleBold: () => {},
                toggleUnderline: () => {},
                toggleBulletList: () => {},
                insertText: () => {},
                appendText: () => {},
                setContent: () => {},
                getMarkdown: () => '',
                getTextBeforeCursor: () => '',
                getCursorScreenCoords: () => ({ x: 0, y: 0 }),
                replaceWordBeforeCursor: () => {},
                getSelectedText: () => '',
                replaceSelection: () => {},
                transformSelectionText: () => {},
                reflowSelection: () => {},
                findMatches: () => 0,
                goToMatch: () => 0,
                replaceCurrent: () => false,
                replaceAll: () => 0,
                jumpToText: () => {},
                focusAtCoords: () => {},
                getScrollElement: () => null,
            };
        }
        return {
            focus: () => { editor.commands.focus(); },
            toggleBold: () => {
                const before = editor.isActive('bold');
                const result = editor.chain().focus().toggleBold().run();
                const after = editor.isActive('bold');
                console.log('[toggleBold] result:', result, 'isActive before:', before, '→ after:', after, 'storedMarks:', editor.state.storedMarks);
            },
            toggleUnderline: () => { editor.chain().focus().toggleUnderline().run(); },
            toggleBulletList: () => { editor.chain().focus().toggleBulletList().run(); },
            insertText: (text: string) => {
                const { from, to } = editor.state.selection;
                editor.commands.insertContentAt({ from, to }, text);
                editor.commands.focus();
            },
            appendText: (text: string) => {
                editor.chain().focus().command(({ tr, dispatch, state }) => {
                    if (dispatch) {
                        const end = state.doc.content.size;
                        tr.insertText('\n\n' + text, end - 1);
                    }
                    return true;
                }).run();
            },
            setContent: (markdown: string) => { editor.commands.setContent(preProcessMarkdown(markdown)); },
            getMarkdown: () => editor.storage?.markdown?.getMarkdown() ?? '',
            getTextBeforeCursor: () => {
                if (!editor.state) return '';
                const { from } = editor.state.selection;
                return editor.state.doc.textBetween(0, from, '\n');
            },
            getCursorScreenCoords: () => {
                if (!editor.state || !editor.view) return { x: 0, y: 0 };
                const { from } = editor.state.selection;
                const coords = editor.view.coordsAtPos(from);
                return { x: coords.left, y: coords.bottom };
            },
            replaceWordBeforeCursor: (query: string, replacement: string) => {
                const { from } = editor.state.selection;
                // Use { type: 'text', text } to avoid TipTap parsing '<' / '>' as HTML tags
                const content = { type: 'text', text: replacement };
                if (query) {
                    editor.chain().focus()
                        .deleteRange({ from: from - query.length, to: from })
                        .insertContent(content)
                        .run();
                } else {
                    editor.chain().focus().insertContent(content).run();
                }
            },
            getSelectedText: () => {
                const { from, to } = editor.state.selection;
                if (from === to) return '';
                return editor.state.doc.textBetween(from, to, '\n');
            },
            replaceSelection: (text: string) => {
                const { from, to } = editor.state.selection;
                if (from === to) return;
                editor.chain().focus().deleteRange({ from, to }).insertContent(text).run();
            },
            transformSelectionText: (fn: (text: string) => string) => {
                const { from, to } = editor.state.selection;
                if (from === to) return;
                const tr = editor.state.tr;
                editor.state.doc.nodesBetween(from, to, (node, pos) => {
                    if (node.isText) {
                        const nodeFrom = Math.max(from, pos);
                        const nodeTo = Math.min(to, pos + node.nodeSize);
                        const textFrom = nodeFrom - pos;
                        const textTo = nodeTo - pos;
                        const original = node.text!.slice(textFrom, textTo);
                        const transformed = fn(original);
                        if (transformed !== original) {
                            tr.replaceWith(nodeFrom, nodeTo, editor.state.schema.text(transformed, node.marks));
                        }
                    }
                });
                editor.view.dispatch(tr);
                editor.commands.focus();
            },
            reflowSelection: () => {
                const { from, to } = editor.state.selection;
                if (from === to) return;

                // Collect paragraph text nodes in selection
                const slice = editor.state.doc.slice(from, to);
                const paragraphTexts: string[] = [];
                slice.content.forEach(node => {
                    if (node.type.name === 'paragraph') {
                        paragraphTexts.push(node.textContent);
                    }
                });

                if (paragraphTexts.length > 1) {
                    // Multiple paragraphs selected — join into one
                    const joined = paragraphTexts
                        .map(t => t.trim())
                        .filter(Boolean)
                        .join(' ')
                        .replace(/\s+/g, ' ')
                        .trim();
                    editor.chain().focus().deleteRange({ from, to }).insertContent(joined).run();
                    return;
                }

                // Single paragraph — fall back to hardBreak removal
                const positions: number[] = [];
                editor.state.doc.nodesBetween(from, to, (node, pos) => {
                    if (node.type.name === 'hardBreak') positions.push(pos);
                });
                if (positions.length === 0) return;
                let tr = editor.state.tr;
                for (let i = positions.length - 1; i >= 0; i--) {
                    const pos = positions[i];
                    tr = tr.replaceWith(pos, pos + 1, editor.schema.text(' '));
                }
                editor.view.dispatch(tr);
            },
            findMatches: (query: string, caseSensitive = false) => {
                if (!query || !editor.state) return 0;
                const q = caseSensitive ? query : query.toLowerCase();
                let count = 0;
                editor.state.doc.descendants(node => {
                    if (node.isText) {
                        let idx = 0;
                        const text = caseSensitive ? node.text! : node.text!.toLowerCase();
                        while ((idx = text.indexOf(q, idx)) !== -1) { count++; idx++; }
                    }
                });
                return count;
            },
            goToMatch: (query: string, index: number, caseSensitive = false) => {
                if (!query || !editor.state) return 0;
                const q = caseSensitive ? query : query.toLowerCase();
                const matches: { from: number; to: number }[] = [];
                editor.state.doc.descendants((node, pos) => {
                    if (node.isText) {
                        let idx = 0;
                        const text = caseSensitive ? node.text! : node.text!.toLowerCase();
                        while ((idx = text.indexOf(q, idx)) !== -1) {
                            matches.push({ from: pos + idx, to: pos + idx + q.length });
                            idx++;
                        }
                    }
                });
                if (!matches.length) return 0;
                const i = ((index % matches.length) + matches.length) % matches.length;
                const { from, to } = matches[i];
                editor.chain().focus().setTextSelection({ from, to }).scrollIntoView().run();
                return matches.length;
            },
            replaceCurrent: (query: string, replacement: string, matchIndex: number, caseSensitive = false) => {
                if (!query || !editor.state) return false;
                const q = caseSensitive ? query : query.toLowerCase();
                const matches: { from: number; to: number }[] = [];
                editor.state.doc.descendants((node, pos) => {
                    if (node.isText) {
                        let idx = 0;
                        const text = caseSensitive ? node.text! : node.text!.toLowerCase();
                        while ((idx = text.indexOf(q, idx)) !== -1) {
                            matches.push({ from: pos + idx, to: pos + idx + q.length });
                            idx++;
                        }
                    }
                });
                if (!matches.length) return false;
                const i = ((matchIndex % matches.length) + matches.length) % matches.length;
                const { from, to } = matches[i];
                const tr = replacement
                    ? editor.state.tr.replaceWith(from, to, editor.state.schema.text(replacement))
                    : editor.state.tr.delete(from, to);
                editor.view.dispatch(tr);
                editor.commands.focus();
                return true;
            },
            replaceAll: (query: string, replacement: string, caseSensitive = false) => {
                if (!query || !editor.state) return 0;
                const q = caseSensitive ? query : query.toLowerCase();
                const matches: { from: number; to: number }[] = [];
                editor.state.doc.descendants((node, pos) => {
                    if (node.isText) {
                        let idx = 0;
                        const text = caseSensitive ? node.text! : node.text!.toLowerCase();
                        while ((idx = text.indexOf(q, idx)) !== -1) {
                            matches.push({ from: pos + idx, to: pos + idx + q.length });
                            idx++;
                        }
                    }
                });
                if (!matches.length) return 0;
                let tr = editor.state.tr;
                for (let i = matches.length - 1; i >= 0; i--) {
                    const { from, to } = matches[i];
                    tr = replacement
                        ? tr.replaceWith(from, to, editor.state.schema.text(replacement))
                        : tr.delete(from, to);
                }
                editor.view.dispatch(tr);
                editor.commands.focus();
                return matches.length;
            },
            jumpToText: (blockText: string, _charOffset: number) => {
                if (!editor.state) { editor.commands.focus(); return; }
                const search = blockText.toLowerCase().slice(0, 60);
                if (!search) { editor.commands.focus(); return; }
                let found: number | null = null;
                editor.state.doc.forEach((node, offset) => {
                    if (found !== null) return;
                    const nodeText = node.textContent.toLowerCase();
                    if (nodeText.includes(search.slice(0, 30))) {
                        found = offset + 1; // start of this block
                    }
                });
                if (found !== null) {
                    // Scroll the block into view, then try posAtCoords for precise cursor
                    editor.chain().focus().setTextSelection(found).scrollIntoView().run();
                } else {
                    editor.commands.focus();
                }
            },
            focusAtCoords: (clientX: number, clientY: number) => {
                if (!editor.view) { editor.commands.focus(); return; }
                const result = editor.view.posAtCoords({ left: clientX, top: clientY });
                if (result != null) {
                    const pos = Math.max(0, Math.min(result.pos, editor.state.doc.content.size));
                    editor.chain().focus().setTextSelection(pos).run();
                } else {
                    editor.commands.focus();
                }
            },
            getScrollElement: () => scrollDivRef.current,
        };
    });

    return (
        <div ref={scrollDivRef} className={`flex-1 overflow-y-auto hide-scrollbar p-3 md:p-8 ${className}`} style={style}>
            <EditorContent editor={editor} className="h-full" />
        </div>
    );
});

RichTextEditor.displayName = 'RichTextEditor';
